import { Controller, type Application, type ControllerConstructor } from '@hotwired/stimulus';

/** Module d'un contrôleur Stimulus, tel que le résout un `import()` dynamique. */
type ControllerModule = { default: ControllerConstructor };

/**
 * Enregistre un contrôleur dont le code n'est téléchargé qu'au premier élément qui le porte.
 *
 * Réservé aux contrôleurs qui embarquent une dépendance lourde (apexcharts, katex, intl-tel-input,
 * imask) : ils ne servent qu'à une poignée d'écrans mais sont déclarés depuis des gabarits communs
 * (thème de formulaire global, page de contact partagée), donc impossibles à cantonner à une seule
 * entrée Encore. Les mettre en différé évite d'en faire payer le poids à toutes les pages.
 *
 * Un stub sans dépendance prend l'identifiant ; à son `initialize()`, il déclenche l'`import()` du
 * vrai module puis le réenregistre sous le même identifiant — Stimulus rebranche alors les éléments
 * déjà présents. C'est le mécanisme du `lazy-controller-loader` de `@symfony/stimulus-bridge`,
 * réécrit ici : ce loader ne s'applique qu'aux contrôleurs découverts par `require.context`, alors
 * que nos entrées les importent nommément (cf. `public.ts` / `account.ts`).
 */
export default function registerLazy (
  application: Application,
  identifier: string,
  load: () => Promise<ControllerModule>,
): void {
  /*
   * Un même identifiant peut être porté par plusieurs éléments de la page : sans ce garde-fou,
   * chacun déclencherait son propre `import()` avant que le vrai contrôleur ne soit enregistré.
   */
  let requested = false;

  application.register(identifier, class extends Controller {
    initialize (): void {
      if (requested) {
        return;
      }
      requested = true;

      void load()
        .then(module => application.register(identifier, module.default))
        .catch((error: unknown) => {
          /*
           * Chunk introuvable (déploiement survenu pendant que l'onglet était ouvert), réseau
           * coupé… Sans ce `catch`, le garde-fou resterait armé : le contrôleur ne s'activerait
           * jamais et rien ne le signalerait. On rouvre donc la porte pour que le prochain montage
           * — un Live Component qui re-rend son DOM, par exemple — retente le téléchargement.
           */
          requested = false;

          console.error(`Stimulus : chargement du contrôleur « ${identifier} » impossible.`, error);
        });
    }
  });
}
